/**
 * ─────────────────────────────────────────────────────────────────────────────
 * @module  public/js/services/contactsStagingLinkedinFinder.js
 * @summary Query Firestore ContactsStaging for a contact's LinkedIn URL
 *
 * @description
 * Looks up the `/ContactsStaging` collection where `crmContactId` or
 * `contactId` equals the provided ID and returns the `linkedinUrl` field from
 * the first match.
 *
 * ## Public API
 * async lookupStagedLinkedinUrl(contactId: string): Promise<string>
 *
 * @author  TopSail AI
 * @version 1.0.0
 * @since   2025-06-12
 * ─────────────────────────────────────────────────────────────────────────────
 */

import {
  query,
  where,
  getDocs,
} from 'https://www.gstatic.com/firebasejs/9.17.2/firebase-firestore.js';
import { colWithTenant } from '../utils/firestoreHelpers.js';
import { resolveRuntimeDi } from '../utils/runtimeDi.js';

/**
 * Fetch the LinkedIn profile URL from the ContactsStaging collection by first
 * matching the `crmContactId` field and falling back to `contactId` if no match
 * is found.
 *
 * @param {string} contactId - Contact ID to look up
 * @param {Record<string, unknown>} [services] - Optional DI overrides.
 * @returns {Promise<string>} LinkedIn URL or empty string if not found
 */
export async function lookupStagedLinkedinUrl(contactId, services = null) {
  if (!contactId) return '';
  try {
    const di = resolveRuntimeDi(services);
    const user = di.auth?.currentUser ?? (await di.auth?.waitForLogin?.());
    const tenantId = user?.tenantId;
    if (!tenantId) {
      console.error('Missing tenant ID');
      throw new Error('Missing tenant ID');
    }
    let q = query(
      colWithTenant(tenantId, 'ContactsStaging'),
      where('crmContactId', '==', contactId)
    );
    let snap = await getDocs(q);
    if (snap.empty) {
      q = query(
        colWithTenant(tenantId, 'ContactsStaging'),
        where('contactId', '==', contactId)
      );
      snap = await getDocs(q);
    }
    if (snap.empty) return '';
    const data = snap.docs[0].data() || {};
    return data.linkedinUrl?.toString().trim() || '';
  } catch (err) {
    console.error('[contactsStagingLinkedinFinder] lookup failed:', err);
    return '';
  }
}
